import { Button } from '@commenda/kakashi';

import { DateFilter } from '../sales-tax/common/filters/DateFilter/DateFilter';
import { FilterValue } from '../sales-tax/common/filters/FilterValue/FilterValue';
import { MultiSelectFilter } from '../sales-tax/common/filters/MultiSelectFilter/MultiSelectFilter';
import { SingleSelectFilter } from '../sales-tax/common/filters/SingleSelectFilter/SingleSelectFilter';
import {
  FilterChangeEvent,
  FilterConfig,
} from '../sales-tax/common/filters/types';
import { getFilterValueLabel } from '../sales-tax/common/filters/utils';

type ControlledFilterBarProps = {
  activeFilters: Record<string, string | string[]>;
  filters: FilterConfig[];
  onFilterChange: (event: FilterChangeEvent) => void;
  onFilterClear: (filterId: string) => void;
  onClearAll: () => void;
  hideLabel?: boolean;
  hideBackground?: boolean;
  _className?: string;
};

export const ControlledFilterBar = ({
  activeFilters,
  filters,
  onFilterChange,
  onFilterClear,
  onClearAll,
  hideLabel = false,
  hideBackground = false,
  _className,
}: ControlledFilterBarProps) => {
  const handleFilterChange = (event: FilterChangeEvent) => {
    onFilterChange?.(event);
  };

  const handleFilterClear = (filterId: string) => {
    onFilterClear?.(filterId);
  };

  const handleClearAll = () => {
    onClearAll?.();
  };

  const renderFilter = (filter: FilterConfig) => {
    switch (filter.type) {
      case 'DATE':
        return (
          <DateFilter
            value={activeFilters[filter.id] as string}
            onChange={(value) =>
              handleFilterChange({
                filterId: filter.id,
                value,
              })
            }
          />
        );
      case 'MULTIPLE':
        return (
          <MultiSelectFilter
            config={filter}
            values={(activeFilters[filter.id] as string[]) || []}
            onChange={(values) =>
              handleFilterChange({
                filterId: filter.id,
                value: values,
              })
            }
          />
        );
      case 'SINGULAR':
        return (
          <SingleSelectFilter
            config={filter}
            value={activeFilters[filter.id] as string}
            onChange={(value) =>
              handleFilterChange({
                filterId: filter.id,
                value,
              })
            }
          />
        );
      default:
        return null;
    }
  };

  return (
    <div
      className={`${hideBackground ? '' : 'p-4 rounded-2xl bg-bg-tertiary'} ${_className}`}
    >
      <div className="flex flex-wrap gap-2 items-center">
        {!hideLabel && (
          <span className="txt-small-medium text-text-quaternary">
            Filter by
          </span>
        )}
        <div className="flex gap-2 items-center">
          {filters.map((filter) => (
            <div key={filter.id} className="flex items-center gap-2">
              {renderFilter(filter)}
              {activeFilters[filter.id] && (
                <FilterValue
                  label={getFilterValueLabel(filter, activeFilters[filter.id])}
                  onRemove={() => handleFilterClear(filter.id)}
                />
              )}
            </div>
          ))}

          {Object.keys(activeFilters).length > 0 && (
            <Button
              size="sm"
              variant="tertiary"
              onClick={handleClearAll}
              className="text-text-quaternary hover:text-text-secondary txt-small-medium"
            >
              Clear all
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
